import { SHEET_MARGIN } from '@app/stationery'

/**
 * Shrink a luach until it holds one page.
 *
 * SELF-CONTAINED BY CONTRACT. The PDF renderer injects this into the browser
 * by stringifying it (`fitToPage.toString()`), so it must not reference
 * anything outside its own body — no imports, no module constants, no helpers.
 * A reference that survives the typechecker dies at the far end, inside a page
 * the renderer has already opened, where the failure is a sheet that came out
 * the wrong size rather than an error anybody sees.
 *
 * `zoom` rather than `transform: scale` because zoom reflows — the columns
 * re-balance at the smaller size instead of being drawn small.
 */
export function fitToPage(
  root: HTMLElement,
  limitPx: number,
  widthPx: number,
  minScale = 0.5,
  step = 0.02,
): number {
  /*
   * Only the SCHEDULE scales. The masthead and footer are the same on every
   * sheet the shul prints, so a Sukkos title should not come out smaller than a
   * Purim one just because more days had to fit underneath it — the header is
   * not a function of the day count. Measure the fixed chrome once, then fit
   * the day sections into what is left.
   */
  const body = root.querySelector<HTMLElement>('.luach__body')
  if (body) {
    body.style.zoom = '1'

    measureTimeColumn(root)

    // Measure with the sheet at its natural height; the page height is applied
    // afterwards, once the scale is known.
    root.style.height = ''
    void root.offsetHeight

    /*
     * Two columns share ONE row grid, so every rule in one column is level
     * with a rule in the other and the page reads across as well as down.
     *
     * The unit is the height most times take, and every heading and line is
     * padded up to a whole number of units. CSS cannot round a height up to a
     * multiple, and a guessed unit is one some line (a room, an address, a
     * label that wraps) eventually exceeds — so it is measured.
     *
     * It is measured at EACH zoom the fit tries, never once up front: zoom
     * reflows, a column holds more of a label the smaller the sheet is set, so
     * a line that wraps at full size can take one row at the size it prints.
     *
     * `luach.css` states each block's height as whole rows and puts what the
     * block lacks of them (`--grid-slack`) above its content.
     */
    let gridOn = body.classList.contains('luach__body--cols-2')
    let unit = 0
    const zoomOf = () => Number.parseFloat(body.style.zoom) || 1
    // How many rows a block takes, and what it lacks of them, from its height
    // in unzoomed pixels.
    const snap = (block: HTMLElement, zoom: number) => {
      const taken = block.getBoundingClientRect().height / zoom
      // The tolerance absorbs sub-pixel rounding, so a block that IS one unit
      // is not counted as a hair over it.
      const span = Math.max(1, Math.ceil(taken / unit - 0.02))
      return { span, slack: Math.max(0, span * unit - taken) }
    }
    const apply = (block: HTMLElement, at: { span: number; slack: number }) => {
      block.style.setProperty('--grid-span', String(at.span))
      block.style.setProperty('--grid-slack', `${at.slack}px`)
    }
    const gridBlocks = () =>
      Array.from(body.querySelectorAll<HTMLElement>('.day__head, .rows > *'))
    // Every block back to its own height.
    const unlay = () => {
      unit = 0
      body.style.removeProperty('--grid-row')
      for (const block of gridBlocks()) {
        block.style.removeProperty('--grid-span')
        block.style.removeProperty('--grid-slack')
      }
      void body.offsetHeight
    }
    const lay = () => {
      if (!gridOn) return
      unlay()
      const zoom = zoomOf()
      // The most common height, taken at the tallest of the rows that share
      // it so none of them is left a hair short of the unit; between two
      // heights equally common, the taller, for the same reason.
      const heights = new Map<number, number[]>()
      for (const row of Array.from(
        body.querySelectorAll('.rows > .row:not(.row--anchor)'),
      )) {
        const h = row.getBoundingClientRect().height / zoom
        const bucket = Math.round(h * 10) / 10
        heights.set(bucket, [...(heights.get(bucket) ?? []), h])
      }
      let most = 0
      let found = 0
      for (const same of heights.values()) {
        if (same.length >= most) {
          const tallest = Math.max(...same)
          if (same.length > most || tallest > found) found = tallest
          most = same.length
        }
      }
      if (found === 0) return
      unit = found
      // Measured together, then applied together: each block is read at its
      // own height before any other block has been given a row.
      const snapped = gridBlocks().map((block) => ({
        block,
        at: snap(block, zoom),
      }))
      body.style.setProperty('--grid-row', `${unit}px`)
      for (const { block, at } of snapped) apply(block, at)
      void body.offsetHeight
    }

    /*
     * (root − body), NOT the sum of the chrome elements' own heights: a box's
     * rect excludes its margins, so summing children drops the footer's
     * margin-top and over-reports the space available — which spills a day onto
     * a second page, silently, because nothing measures wrong loudly.
     */
    const chrome =
      root.getBoundingClientRect().height - body.getBoundingClientRect().height
    const available = limitPx - chrome

    /*
     * Mark a day that continues in the next column.
     *
     * Only the browser knows where the break landed, so this runs AFTER the
     * layout: find the first row of a day that sits in a different column from
     * the day's heading, and label it. Without it a column can open with three
     * bare times and no indication of which day they belong to.
     *
     * Re-run after marking, because the marker itself takes height.
     */
    const markContinuations = (): boolean => {
      let added = false
      for (const stale of Array.from(
        body.querySelectorAll('.day__continued'),
      )) {
        stale.remove()
      }
      for (const day of Array.from(body.querySelectorAll('.day'))) {
        const head = day.querySelector('.day__head')
        const rows = day.querySelector('.rows')
        if (!head || !rows) continue
        const headLeft = Math.round(head.getBoundingClientRect().left)
        for (const row of Array.from(rows.children)) {
          const left = Math.round(row.getBoundingClientRect().left)
          // A different column — the first row of the continuation.
          if (Math.abs(left - headLeft) > 4) {
            const name = day.querySelector('.day__name')?.textContent ?? ''
            const marker = document.createElement('div')
            marker.className = 'day__continued'
            marker.textContent = `${name} (המשך)`
            rows.insertBefore(marker, row)
            // Placed at the fitted zoom, so its height is read back through it.
            if (unit > 0) {
              // Measured at its natural height, before its row applies.
              marker.style.height = 'auto'
              const at = snap(marker, zoomOf())
              marker.style.removeProperty('height')
              apply(marker, at)
            }
            added = true
            break
          }
        }
      }
      return added
    }

    /* Give the sheet exactly one page to fill, so the footer sits at its foot
       rather than under the last line. Pixels, never a percentage. */
    const fill = (scale: number) => {
      root.style.height = `${limitPx}px`
      return scale
    }

    // The scales tried, largest first, counted in whole steps and rounded, so
    // the floor itself is tried rather than stepped past by float drift.
    const steps = Math.round((1 - minScale) / step)
    // The largest scale at which the body holds, or null where none does.
    const search = (): number | null => {
      for (let i = 0; i <= steps; i++) {
        const scale = Math.max(minScale, Number((1 - i * step).toFixed(4)))
        body.style.zoom = String(scale)
        lay()
        if (body.getBoundingClientRect().height <= available) return scale
      }
      return null
    }

    // Two passes at most: fit, mark the continuations, then re-fit because the
    // markers add height. A third pass would not converge on anything new.
    for (let pass = 0; pass < 2; pass++) {
      let fitted = search()
      // A sheet the row grid will not let fit is fitted without it: level
      // rules are not worth a line lost off the page.
      if (fitted === null && gridOn) {
        gridOn = false
        unlay()
        fitted = search()
      }
      if (fitted === null) {
        body.style.zoom = String(minScale)
        fitted = minScale
      }
      root.style.height = ''
      const added = markContinuations()
      if (!added || pass === 1) return fill(fitted)
    }
    return fill(minScale)
  }

  // No body element to isolate — fall back to scaling the whole sheet.
  for (let scale = 1; scale >= minScale; scale -= step) {
    root.style.zoom = String(scale)
    /*
     * Widen as we shrink, so the sheet still occupies the full printable width
     * after zoom rather than narrowing along with it.
     *
     * Measured effect today: none. The sheet's inner blocks are capped at
     * `--measure`, so widening the outer element does not widen the content —
     * every occasion resolves to the same scale with or without this line. It
     * is kept because the cap is a typographic choice that may be relaxed for
     * print, at which point the compensation starts to matter. Remove it if
     * that never happens.
     */
    root.style.width = `${widthPx / scale}px`
    // Force layout before measuring, or we read the pre-zoom height.
    void root.offsetHeight
    // `zoom` scales the rendered box, so the measured height is already in
    // post-zoom pixels — compare it against the page directly.
    if (root.getBoundingClientRect().height <= limitPx) return scale
  }
  root.style.zoom = String(minScale)
  root.style.width = `${widthPx / minScale}px`
  return minScale
}

/**
 * Size the time column to the widest time actually on this sheet.
 *
 * A fixed width overflows a paired row ("9:05 / 9:45", two opinions on one
 * line); measuring gives every time on the sheet one edge — the point of a
 * fixed column — without a guessed width some occasion eventually exceeds. Runs
 * on screen AND inside the fit pass, so the two never diverge. Measured with the
 * track at max-content so each row reports its natural width rather than the
 * width it is being held to.
 */
export function measureTimeColumn(root: HTMLElement): void {
  const body = root.querySelector<HTMLElement>('.luach__body')
  if (!body) return
  body.style.setProperty('--time-col', 'max-content')
  void body.offsetHeight
  let widest = 0
  for (const t of Array.from(body.querySelectorAll('.row__time'))) {
    widest = Math.max(widest, t.getBoundingClientRect().width)
  }
  body.style.setProperty(
    '--time-col',
    widest > 0 ? `${Math.ceil(widest) + 2}px` : '',
  )
}

/** Letter, at CSS's 96 dpi. */
export const LETTER = { width: 8.5 * 96, height: 11 * 96 }

/**
 * Page margins, in inches — the stationery's sheet margin, which `print.css`
 * declares in `@page`. Held as a number too, because the fit measures against
 * the printable area and a stylesheet is not something it can read.
 */
export const PAGE_MARGIN = SHEET_MARGIN

export const PRINTABLE = {
  width: Math.round((8.5 - PAGE_MARGIN.side * 2) * 96),
  height: Math.round((11 - PAGE_MARGIN.top * 2) * 96),
}
